import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiSave, FiLoader, FiArrowLeft, FiFileText, FiCpu } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi } from '../../services/api';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useCache } from '../../contexts/CacheContext';

const JobEdit = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { invalidateJobCache } = useCache();
  
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    description: '',
    sourceUrl: '',
    summary: '',
    responsibilities: [],
    requirements: [],
    skills: [],
    location: '',
    employment_type: '',
    notes: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Fetch job data on component mount
  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setLoading(true);
        const response = await jobApi.getJobById(jobId);
        
        // Set form data from response
        setFormData({
          title: response.data.title || '',
          company: response.data.company || '',
          description: response.data.description || '',
          sourceUrl: response.data.sourceUrl || '',
          summary: response.data.summary || '',
          responsibilities: response.data.responsibilities || [],
          requirements: response.data.requirements || [],
          skills: response.data.skills || [],
          location: response.data.location || '',
          employment_type: response.data.employment_type || '',
          notes: response.data.notes || ''
        });
      } catch (error) {
        console.error('Error fetching job data:', error);
        toast.error('Failed to load job data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchJobData();
  }, [jobId]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Job title is required';
    }
    
    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Job description is required';
    }
    
    if (formData.sourceUrl && !isValidUrl(formData.sourceUrl)) {
      newErrors.sourceUrl = 'Please enter a valid URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };
  
  const handleArrayItemChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    
    setFormData({
      ...formData,
      [field]: newArray
    });
  };
  
  const handleAddArrayItem = (field) => {
    setFormData({
      ...formData,
      [field]: [...formData[field], '']
    });
  };
  
  const handleRemoveArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    
    setFormData({
      ...formData,
      [field]: newArray
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSaving(true);
      
      // Create object with all fields for update
      const updateData = {
        title: formData.title,
        company: formData.company,
        description: formData.description,
        sourceUrl: formData.sourceUrl,
        summary: formData.summary,
        responsibilities: formData.responsibilities,
        requirements: formData.requirements,
        skills: formData.skills,
        location: formData.location,
        employment_type: formData.employment_type,
        notes: formData.notes
      };
      
      // Update job information (includes notes)
      await jobApi.updateJob(jobId, updateData);
      
      // Invalidate the job cache so the job list will be refreshed
      invalidateJobCache();
      
      toast.success('Job updated successfully!');
      // Redirect to jobs page with the updated job ID as a highlight parameter
      navigate(`/jobs/saved?highlight=${jobId}`);
    } catch (error) {
      console.error('Error updating job:', error);
      toast.error('Failed to update job information');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  const copy = pageDescriptions.jobEdit;

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title={`${copy.title}: ${formData.title || 'Job'}`}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <button
            type="button"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            onClick={() => navigate('/jobs/saved')}
          >
            <FiArrowLeft className="mr-1 h-4 w-4" />
            Back to Saved Jobs
          </button>
        }
      />
      <div className="bg-white shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            Edit Job: {formData.title}
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Update job information and notes
          </p>
        </div>
        
        <div className="border-t border-gray-200">
          <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              {/* Job Title */}
              <div className="sm:col-span-3">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Job Title *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="title"
                    id="title"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.title ? 'border-red-300' : ''
                    }`}
                    value={formData.title}
                    onChange={handleChange}
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                  )}
                </div>
              </div>
              
              {/* Company */}
              <div className="sm:col-span-3">
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company *
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="company"
                    id="company"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.company ? 'border-red-300' : ''
                    }`}
                    value={formData.company}
                    onChange={handleChange}
                  />
                  {errors.company && (
                    <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                  )}
                </div>
              </div>
              
              {/* Location */}
              <div className="sm:col-span-3">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                  Location
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="location"
                    id="location"
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.location}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              {/* Employment Type */}
              <div className="sm:col-span-3">
                <label htmlFor="employment_type" className="block text-sm font-medium text-gray-700">
                  Employment Type
                </label>
                <div className="mt-1">
                  <select
                    id="employment_type"
                    name="employment_type"
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.employment_type}
                    onChange={handleChange}
                  >
                    <option value="">Select Type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>
              
              {/* Source URL */}
              <div className="sm:col-span-6">
                <label htmlFor="sourceUrl" className="block text-sm font-medium text-gray-700">
                  Source URL
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="sourceUrl"
                    id="sourceUrl"
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.sourceUrl ? 'border-red-300' : ''
                    }`}
                    value={formData.sourceUrl}
                    onChange={handleChange}
                  />
                  {errors.sourceUrl ? (
                    <p className="mt-1 text-sm text-red-600">{errors.sourceUrl}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      The URL where you found this job posting
                    </p>
                  )}
                </div>
              </div>
              
              {/* Job Description */}
              <div className="sm:col-span-6">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Job Description *
                </label>
                <div className="mt-1">
                  <textarea
                    id="description"
                    name="description"
                    rows={8}
                    className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.description ? 'border-red-300' : ''
                    }`}
                    value={formData.description}
                    onChange={handleChange}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
              </div>
              
              {/* Summary */}
              <div className="sm:col-span-6">
                <label htmlFor="summary" className="block text-sm font-medium text-gray-700">
                  Job Summary
                </label>
                <div className="mt-1">
                  <textarea
                    id="summary"
                    name="summary"
                    rows={3}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.summary}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              {/* Notes */}
              <div className="sm:col-span-6">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <div className="mt-1">
                  <textarea
                    id="notes"
                    name="notes"
                    rows={4}
                    className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Add your personal notes about this job opportunity..."
                  />
                </div>
              </div>
              
              {/* Skills */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Required Skills
                </label>
                {formData.skills.map((skill, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={skill}
                      onChange={(e) => handleArrayItemChange('skills', index, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('skills', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem('skills')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Skill
                </button>
              </div>
              
              {/* Responsibilities */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Responsibilities
                </label>
                {formData.responsibilities.map((resp, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={resp}
                      onChange={(e) => handleArrayItemChange('responsibilities', index, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('responsibilities', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem('responsibilities')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Responsibility
                </button>
              </div>
              
              {/* Requirements */}
              <div className="sm:col-span-6">
                <label className="block text-sm font-medium text-gray-700">
                  Requirements
                </label>
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex mt-2">
                    <input
                      type="text"
                      className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      value={req}
                      onChange={(e) => handleArrayItemChange('requirements', index, e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveArrayItem('requirements', index)}
                      className="ml-2 inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddArrayItem('requirements')}
                  className="mt-2 inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Add Requirement
                </button>
              </div>
            </div>
            
            <div className="pt-5">
              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => navigate('/jobs')}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <FiSave className="-ml-1 mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobEdit;